<script setup lang="ts">
/**
 * Application shell.
 *
 * Two things live here and nowhere else.
 *
 * **The single `<main>` landmark.** Views render plain sections inside it. A second `<main>` in a
 * view would give the page two "main content" landmarks, and assistive technology has no way to
 * decide which one is the actual content.
 *
 * **The baseline head call.** `useRouteHead` comes from `@bayudwiyansatria/vue`; the builder it is
 * given comes from `@bayudwiyansatria/seo`. That one line is the only place the two packages meet,
 * and neither knows about the other — see `src/config/seo.ts`.
 *
 * Driving it from the shell means every route gets a title, canonical URL, robots directive and
 * structured data from its route metadata without each view remembering to ask. A view with values
 * that depend on fetched data can call `useRouteHead` again with its own builder; the later
 * registration wins, because unhead resolves tags by key.
 */

/**
 * Vue
 */
import { useI18n } from 'vue-i18n'

/**
 * Components
 */
import AppFooter from '@/components/layouts/AppFooter.vue'
import AppHeader from '@/components/layouts/AppHeader.vue'

/**
 * Composables
 */
import { useRouteHead } from '@bayudwiyansatria/vue/router'

/**
 * Config
 */
import { seo } from '@/config/seo'

useRouteHead(facts => seo.head(facts))

/*
 * The skip link is translated like everything else. It is the first thing a keyboard user reaches,
 * so it is the last place to leave an untranslated string.
 */
const { t } = useI18n()
</script>

<template>
  <div class="flex min-h-screen flex-col bg-background text-foreground">
    <a href="#main" class="skip-link">{{ t('nav.skipToContent') }}</a>

    <AppHeader />

    <main id="main" class="flex-1">
      <router-view />
    </main>

    <AppFooter />
  </div>
</template>
